import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Users, BarChart3, Palette, Smartphone, Shield, CircleDot, Wifi, ShoppingBag, Tag } from 'lucide-react';
import { PlexataIcon } from '../components/PlexataLogo';

const Landing = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 lg:px-16 py-5 border-b border-gray-100">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
          <PlexataIcon size={28} />
          <span className="font-heading text-xl font-medium tracking-tight text-gray-900">plexata</span>
        </Link>
        <div className="flex items-center gap-5">
          <Link to="/login" className="text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors" data-testid="nav-login">
            Log in
          </Link>
          <Link to="/store" className="bg-gray-900 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-black transition-colors" data-testid="nav-get-started">
            Get Started
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle, #1a1a1a 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }}></div>
        <div className="relative max-w-5xl mx-auto px-6 pt-28 pb-24 text-left">
          <p className="text-sm font-semibold tracking-wider uppercase text-indigo-600 mb-6 opacity-0 animate-fade-up" data-testid="hero-badge">
            The future of networking
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95] text-gray-900 mb-8 opacity-0 animate-fade-up stagger-1" data-testid="hero-heading">
            Tap. Connect.<br />
            Never miss a lead.
          </h1>
          <p className="text-base lg:text-lg text-gray-600 max-w-xl leading-relaxed mb-12 opacity-0 animate-fade-up stagger-2">
            Plexata NFC rings let you share your digital business card with a single tap. No app needed. Just tap any smartphone and instantly exchange contact info.
          </p>
          <div className="flex flex-col sm:flex-row items-start gap-4 opacity-0 animate-fade-up stagger-3">
            <Link to="/store" className="bg-gray-900 text-white px-8 py-4 rounded-full font-semibold text-sm hover:bg-black transition-all hover:scale-105 active:scale-95 flex items-center gap-2 shadow-lg shadow-gray-900/10" data-testid="hero-cta">
              Order Your Ring <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/u/demouser" className="border border-gray-200 text-gray-600 px-8 py-4 rounded-full font-semibold text-sm hover:border-gray-400 hover:text-gray-900 transition-all" data-testid="hero-demo">
              See Demo Card
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-24 md:py-32">
          <p className="text-sm font-semibold tracking-wider uppercase text-indigo-600 mb-4">How it works</p>
          <h2 className="font-heading text-3xl md:text-4xl font-normal tracking-tight text-gray-900 mb-16 max-w-lg">
            Networking made effortless.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { step: '01', title: 'Wear Your Ring', desc: 'Slip on your Plexata NFC ring. It works with any outfit and never needs charging.' },
              { step: '02', title: 'Tap Any Phone', desc: 'When you meet someone, simply tap your ring to their smartphone. Works with iPhone and Android.' },
              { step: '03', title: 'Capture Every Lead', desc: 'They see your digital card and share their info back. Every contact saved to your dashboard.' },
            ].map((item, i) => (
              <div key={i} className="relative">
                <span className="text-6xl font-heading font-light text-gray-100 absolute -top-4 -left-2">{item.step}</span>
                <div className="relative pt-8">
                  <h3 className="font-heading text-xl font-medium text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-24 md:py-32">
        <p className="text-sm font-semibold tracking-wider uppercase text-indigo-600 mb-4">Features</p>
        <h2 className="font-heading text-3xl md:text-4xl font-normal tracking-tight text-gray-900 mb-16 max-w-lg">
          More than just a ring.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            { icon: Wifi, title: 'NFC Technology', desc: 'No app required. Your ring works with any NFC-enabled smartphone instantly.' },
            { icon: Users, title: 'Lead Capture', desc: 'Visitors share their contact info with you. Every connection saved automatically.' },
            { icon: Zap, title: 'Auto SMS', desc: 'Send automatic follow-up texts when someone connects with you.', pro: true },
            { icon: BarChart3, title: 'Analytics', desc: 'Track taps, profile views, and lead conversion rates in real-time.' },
            { icon: Palette, title: 'Custom Profiles', desc: 'Choose from curated themes or customize colors to match your personal brand.' },
            { icon: Smartphone, title: 'Universal', desc: 'Works with iPhone, Android, and any NFC-enabled device. No batteries needed.' },
          ].map((f, i) => (
            <div key={i} className="group p-6 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center mb-5 group-hover:bg-indigo-50 transition-colors duration-300">
                <f.icon className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors duration-300" />
              </div>
              <h3 className="font-heading text-lg font-medium text-gray-900 mb-2 flex items-center gap-2">
                {f.title}
                {f.pro && <span className="text-[10px] bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-body font-bold">PRO</span>}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ring Showcase */}
      <section className="bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-24 md:py-32">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold tracking-wider uppercase text-indigo-400 mb-4">The Ring</p>
              <h2 className="font-heading text-3xl md:text-4xl font-normal tracking-tight mb-6">
                Sleek. Durable. Powerful.
              </h2>
              <p className="text-gray-400 leading-relaxed mb-8">
                Crafted from premium materials, the Plexata ring is water-resistant, scratch-proof, and designed to last. 
                Available in multiple sizes and finishes to match your style.
              </p>
              <ul className="space-y-3 text-sm mb-10">
                {[
                  'No charging required - works forever',
                  'Water-resistant & scratch-proof',
                  'Multiple sizes (US 7-13)',
                  'Available in Gold, Silver, or Black',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <CircleDot className="w-4 h-4 text-indigo-400" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row items-start gap-3">
                <Link to="/store" className="bg-white text-gray-900 px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-gray-100 transition-all hover:scale-105 active:scale-95 flex items-center gap-2" data-testid="showcase-shop-btn">
                  <ShoppingBag className="w-4 h-4" /> Order Ring — $199
                </Link>
                <div className="flex items-center gap-2 bg-indigo-500/20 text-indigo-300 px-4 py-3 rounded-full text-xs font-semibold" data-testid="showcase-promo">
                  <Tag className="w-3.5 h-3.5" /> Use PLEXATA50 for $50 off
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="w-64 h-64 rounded-full bg-gradient-to-br from-gray-800 to-gray-700 flex items-center justify-center border border-gray-700">
                <div className="w-48 h-48 rounded-full bg-gradient-to-br from-gray-700 to-gray-600 flex items-center justify-center border border-gray-600">
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-gray-600 to-gray-500 flex items-center justify-center">
                    <PlexataIcon size={48} className="opacity-80" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 py-24 md:py-32 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-normal tracking-tight text-gray-900 mb-4">Ready to network smarter?</h2>
          <p className="text-sm text-gray-500 mb-10">Order your Plexata ring and start connecting instantly.</p>
          <Link to="/store" className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-full font-semibold text-sm hover:bg-black transition-all hover:scale-105 active:scale-95 shadow-lg shadow-gray-900/10">
            Order Your Ring <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 text-center">
        <div className="flex items-center justify-center gap-2">
          <PlexataIcon size={20} />
          <span className="text-xs text-gray-400 font-medium">Plexata</span>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
